import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Upload, Button, Select, Input, DatePicker, Tag, Empty, Progress, Alert, Tooltip,
} from 'antd';
import {
  InboxOutlined, DeleteOutlined, CloudUploadOutlined, FileTextOutlined,
  CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import axiosInstance from '../variables/api/axiosInstance';
import SinistreSearch from './SinistreSearch';
import { TYPES_DOC_GED } from '../variables/constants';
import { indexationEnCours, attendreIndexation } from '../services/gedIndexation';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { Dragger } = Upload;

const TAGS_SUGGERES = [
  'URGENT', 'PIECE_MANQUANTE', 'A_REVOIR', 'DOUBLON', 'SCAN_REFAIT',
];

/* Même signalétique que la checklist du dossier (ChecklistPieces) : l'agent doit
   reconnaître la même pièce d'un écran à l'autre. */
const TagObligatoire = () => {
  const { t } = useTranslation('commun');
  return (
    <Tag color="red" style={{ borderRadius: 20, fontSize: 10, marginInlineEnd: 0 }}>{t('gedUploader.obligatoire')}</Tag>
  );
};

/**
 * GedUploader - composant réutilisable OssanGED by OSSANASUR.
 *
 * Props :
 *   sinistreId?       UUID du sinistre (si pré-empli, sélecteur masqué)
 *   onUploaded?(docs) callback après upload réussi
 */
export default function GedUploader({ sinistreId: sinistreIdProp, onUploaded }) {
  const { t } = useTranslation('commun');
  const [sinistreId, setSinistreId] = useState(sinistreIdProp || '');
  const [victimes, setVictimes] = useState([]);
  const [dossiersReclamation, setDossiersReclamation] = useState({});
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [globalProgress, setGlobalProgress] = useState(0);
  const [typesPieces, setTypesPieces] = useState([]);
  const [typeDommageSinistre, setTypeDommageSinistre] = useState(null);

  useEffect(() => {
    if (sinistreIdProp) setSinistreId(sinistreIdProp);
  }, [sinistreIdProp]);

  // Charger les victimes quand sinistreId change
  useEffect(() => {
    if (!sinistreId) { setVictimes([]); return; }
    axiosInstance.get(`/v1/victimes/sinistre/${sinistreId}`)
      .then(r => setVictimes(r.data?.data || []))
      .catch(() => setVictimes([]));
  }, [sinistreId]);

  // Paramétrage des types de pièces : sert à marquer « Obligatoire » les types de
  // document attendus dans la checklist de réclamation. Chargé une fois - la liste
  // est courte et ne bouge qu'en paramétrage.
  useEffect(() => {
    axiosInstance.get('/v1/parametrage/pieces')
      .then(r => setTypesPieces((r.data?.data || []).filter(t => t.actif)))
      .catch(() => setTypesPieces([]));
  }, []);

  // Le type de dommage du sinistre est le repli pour une victime NEUTRE (cf. règle
  // backend resolveTypeDommage) : sans lui, aucune obligation n'est déterminable.
  useEffect(() => {
    if (!sinistreId) { setTypeDommageSinistre(null); return; }
    axiosInstance.get(`/v1/sinistres/${sinistreId}`)
      .then(r => setTypeDommageSinistre(r.data?.data?.typeDommage || null))
      .catch(() => setTypeDommageSinistre(null));
  }, [sinistreId]);

  /**
   * Codes GED des pièces obligatoires pour la victime choisie sur un fichier.
   *
   * Réplique la règle qui construit la checklist côté backend
   * (PiecesAdministrativesServiceImpl.synchroniserPieces) : le type de dommage vient
   * du type de victime (BLESSE / DECEDE → CORPOREL), sinon du sinistre ; MIXTE prend
   * toutes les pièces actives, les autres prennent les communes plus les leurs.
   *
   * Tant qu'aucune victime n'est choisie, l'obligation n'est pas décidable : on
   * n'affiche alors aucun tag plutôt qu'un tag faux.
   */
  const obligatoiresPourVictime = useCallback((victimeId) => {
    if (!victimeId) return new Set();
    const victime = victimes.find(v => v.victimeTrackingId === victimeId);
    if (!victime) return new Set();
    const td = (victime.typeVictime === 'BLESSE' || victime.typeVictime === 'DECEDE')
      ? 'CORPOREL'
      : typeDommageSinistre;
    if (!td) return new Set();
    return new Set(
      typesPieces
        .filter(t => t.obligatoire && t.typeDocumentGed)
        .filter(t => td === 'MIXTE' || !t.typeDommage || t.typeDommage === td)
        .map(t => t.typeDocumentGed),
    );
  }, [victimes, typesPieces, typeDommageSinistre]);

  // Charger dossiers reclamation pour chaque victime utilisée
  const loadDossiersForVictime = useCallback(async (victimeId) => {
    if (!victimeId || dossiersReclamation[victimeId]) return;
    try {
      const r = await axiosInstance.get(`/v1/dossiers-reclamation/victime/${victimeId}`);
      setDossiersReclamation(prev => ({ ...prev, [victimeId]: r.data?.data || [] }));
    } catch (_) {
      setDossiersReclamation(prev => ({ ...prev, [victimeId]: [] }));
    }
  }, [dossiersReclamation]);

  // Ajout fichiers dans la liste
  const handleBeforeUpload = (file) => {
    if (file.type !== 'application/pdf') {
      toast.error(t('gedUploader.seulsPdf', { nom: file.name }));
      return Upload.LIST_IGNORE;
    }
    const blobUrl = URL.createObjectURL(file);
    setFiles(prev => [...prev, {
      key: `${file.uid || Date.now()}-${file.name}`,
      file,
      blobUrl,
      titre: file.name.replace(/\.pdf$/i, ''),
      typeDocument: '',
      dateDocument: null,
      victimeId: '',
      dossierReclamationId: '',
      tagsExtra: [],
      status: 'pending',
      progress: 0,
      error: null,
    }]);
    return false; // Empêche l'upload auto d'antd
  };

  const removeFile = (key) => {
    setFiles(prev => {
      const target = prev.find(f => f.key === key);
      if (target?.blobUrl) URL.revokeObjectURL(target.blobUrl);
      return prev.filter(f => f.key !== key);
    });
  };

  const updateFile = (key, patch) => {
    setFiles(prev => prev.map(f => f.key === key ? { ...f, ...patch } : f));
    if (patch.victimeId) loadDossiersForVictime(patch.victimeId);
  };

  const clearAll = () => {
    files.forEach(f => f.blobUrl && URL.revokeObjectURL(f.blobUrl));
    setFiles([]);
  };

  // Validation minimale avant upload - victime requise quand des victimes existent (sans elle, l'auto-association est impossible)
  const isFileReady = (f) => f.titre && f.typeDocument && (victimes.length === 0 || f.victimeId);
  const allReady = useMemo(() => files.length > 0 && files.every(f => isFileReady(f)), [files, victimes]);

  const uploadAll = async () => {
    if (!sinistreId) { toast.error(t('gedUploader.selectionnezSinistre')); return; }
    if (!allReady) { toast.error(t('gedUploader.renseignezChamps')); return; }

    setUploading(true);
    let completed = 0;
    const uploaded = [];
    // { key, trackingId } des documents transferes mais pas encore indexes.
    // Correles par la cle du fichier, jamais par le titre : deux fichiers
    // peuvent porter le meme titre.
    const enIndexation = [];

    for (const f of files) {
      if (f.status === 'done') { completed++; continue; }
      updateFile(f.key, { status: 'uploading', progress: 0, error: null });
      try {
        const metadata = {
          titre: f.titre,
          typeDocument: f.typeDocument,
          dateDocument: f.dateDocument ? f.dateDocument.format('YYYY-MM-DD') : null,
          sinistreTrackingId: sinistreId,
          victimeTrackingId: f.victimeId || null,
          dossierReclamationTrackingId: f.dossierReclamationId || null,
          tagsExtra: f.tagsExtra || [],
        };
        const formData = new FormData();
        formData.append('file', f.file);
        formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        const res = await axiosInstance.post('/v1/ged/documents', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            updateFile(f.key, { progress: pct });
          },
        });
        const doc = res.data?.data;
        // Transfere n'est pas indexe : la GED (Paperless) consomme en asynchrone.
        // On marque « indexation » et on sondera apres la boucle, en parallele -
        // sonder ici serialiserait l'attente de chaque fichier.
        if (indexationEnCours(doc)) {
          updateFile(f.key, { status: 'indexing', progress: 100 });
          enIndexation.push({ key: f.key, trackingId: doc.ossanGedDocumentTrackingId });
        } else {
          updateFile(f.key, { status: 'done', progress: 100 });
        }
        uploaded.push(doc);
      } catch (err) {
        const msg = err?.response?.data?.message || err.message || t('gedUploader.erreurUpload');
        updateFile(f.key, { status: 'error', error: msg });
      } finally {
        completed++;
        setGlobalProgress(Math.round((completed / files.length) * 100));
      }
    }

    setUploading(false);
    const ok = uploaded.length;
    const fail = files.length - ok;
    if (ok > 0) toast.success(t('gedUploader.documentsUploades', { count: ok }));
    if (fail > 0) toast.error(t('gedUploader.documentsEnEchec', { count: fail }));
    if (onUploaded && ok > 0) onUploaded(uploaded);

    // Les documents encore en indexation : on suit leur resolution en parallele
    // et sans bloquer - l'agent voit l'etat basculer seul, plus besoin de
    // recharger la page pour savoir si le document est bien arrive.
    if (enIndexation.length > 0) {
      const n = enIndexation.length;
      toast.info(t('gedUploader.indexationEnCours', { count: n }));
      await Promise.all(enIndexation.map(async ({ key, trackingId }) => {
        const id = await attendreIndexation(trackingId);
        updateFile(key, { status: id ? 'done' : 'indexing_slow' });
      }));
    }
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        message={t('gedUploader.indexationAutomatique')}
        style={{ marginBottom: 16, borderRadius: 10 }}
      />

      {/* ── Sélecteur sinistre (masqué si pré-empli) ── */}
      {!sinistreIdProp && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#374151' }}>{t('gedUploader.sinistre')}</div>
          <SinistreSearch
            value={sinistreId || undefined}
            onChange={(v) => setSinistreId(v || '')}
            placeholder={t('gedUploader.placeholderSinistre')}
          />
        </div>
      )}

      {/* ── Dropzone ── */}
      <Dragger
        multiple
        accept="application/pdf"
        beforeUpload={handleBeforeUpload}
        showUploadList={false}
        disabled={!sinistreId || uploading}
        style={{ borderRadius: 10, background: '#fafafa' }}
      >
        <p style={{ fontSize: 40, color: '#1a6b3c', margin: 0 }}><InboxOutlined /></p>
        <p style={{ fontWeight: 600, fontSize: 15, margin: '8px 0 4px' }}>
          {t('gedUploader.glisserDeposer')}
        </p>
        <p style={{ color: '#6b7280', fontSize: 12 }}>
          {!sinistreId ? t('gedUploader.sinistreDabord') : t('gedUploader.plusieursFichiers')}
        </p>
      </Dragger>

      {/* ── Liste fichiers avec preview ── */}
      {files.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {t('gedUploader.fichiersAUploader', { count: files.length })}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button icon={<DeleteOutlined />} onClick={clearAll} disabled={uploading}>
                {t('gedUploader.vider')}
              </Button>
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                loading={uploading}
                disabled={!allReady}
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
                onClick={uploadAll}
              >
                {t('gedUploader.uploaderTout', { nombre: files.length })}
              </Button>
            </div>
          </div>

          {uploading && (
            <Progress percent={globalProgress} strokeColor="#1a6b3c" style={{ marginBottom: 12 }} />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {files.map((f, idx) => (
              <FileCard
                key={f.key}
                index={idx + 1}
                total={files.length}
                file={f}
                victimes={victimes}
                obligatoires={obligatoiresPourVictime(f.victimeId)}
                dossiersReclamation={dossiersReclamation[f.victimeId] || []}
                onUpdate={(patch) => updateFile(f.key, patch)}
                onRemove={() => removeFile(f.key)}
                disabled={uploading}
              />
            ))}
          </div>
        </div>
      )}

      {files.length === 0 && (
        <div style={{ marginTop: 20 }}>
          <Empty description={t('gedUploader.aucunFichier')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}
    </div>
  );
}

/* ── Carte par fichier : preview PDF + formulaire ─────────────────────────── */
function FileCard({ index, total, file, victimes, obligatoires, dossiersReclamation, onUpdate, onRemove, disabled }) {
  const { t } = useTranslation('commun');
  const { titre, typeDocument, dateDocument, victimeId, dossierReclamationId, tagsExtra, status, progress, error, blobUrl } = file;

  const statusIcon = {
    pending: <FileTextOutlined style={{ color: '#6b7280' }} />,
    uploading: <LoadingOutlined style={{ color: '#1a6b3c' }} />,
    indexing: <LoadingOutlined style={{ color: '#d97706' }} />,
    indexing_slow: <ClockCircleOutlined style={{ color: '#d97706' }} />,
    done: <CheckCircleOutlined style={{ color: '#16a34a' }} />,
    error: <CloseCircleOutlined style={{ color: '#ef4444' }} />,
  }[status];

  /* Le libelle separe l'envoi de l'indexation : l'agent croyait l'envoi echoue
     alors que la GED n'avait pas fini de consommer le document. */
  const statusLibelle = {
    uploading: t('gedUploader.statuts.envoi'),
    indexing: t('gedUploader.statuts.indexation'),
    indexing_slow: t('gedUploader.statuts.indexationLongue'),
    done: t('gedUploader.statuts.archive'),
    error: t('gedUploader.statuts.echec'),
  }[status];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 1.2fr)',
      gap: 16,
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      padding: 12,
      background: '#fff',
    }}>
      {/* Preview PDF */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
            {statusIcon} {t('gedUploader.fichierIndex', { index, total, nom: file.file.name })}
            {statusLibelle && (
              <span style={{ marginLeft: 8, fontStyle: 'italic' }}>{statusLibelle}</span>
            )}
          </div>
          <Tooltip title={t('gedUploader.retirer')}>
            <Button type="text" danger size="small" icon={<DeleteOutlined />}
              onClick={onRemove} disabled={disabled || status === 'uploading'} />
          </Tooltip>
        </div>
        <iframe
          src={blobUrl}
          title={file.file.name}
          style={{ width: '100%', height: 380, border: '1px solid #e5e7eb', borderRadius: 6 }}
        />
        {status === 'uploading' && <Progress percent={progress} size="small" strokeColor="#1a6b3c" />}
        {status === 'error' && (
          <Alert type="error" message={error} showIcon style={{ borderRadius: 6 }} />
        )}
      </div>

      {/* Formulaire de tagging */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Field label={t('gedUploader.champs.titre')} required>
          <Input value={titre} onChange={(e) => onUpdate({ titre: e.target.value })} disabled={disabled} />
        </Field>

        {/* Le libellé des options reste une chaîne : la recherche (filterOption) et
            l'affichage de la valeur choisie s'appuient dessus. Le tag est ajouté au
            rendu, pas au libellé. */}
        <Field label={t('gedUploader.champs.typeDocument')} required>
          <Select value={typeDocument || undefined} onChange={(v) => onUpdate({ typeDocument: v })}
            placeholder={t('gedUploader.champs.choisirType')} disabled={disabled} style={{ width: '100%' }}
            showSearch filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
            options={TYPES_DOC_GED.map(t => ({ value: t.value, label: t.label }))}
            optionRender={(opt) => (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ flex: 1, minWidth: 0 }}>{opt.label}</span>
                {obligatoires?.has(opt.value) && <TagObligatoire />}
              </span>
            )}
            labelRender={(opt) => (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {opt.label}
                {obligatoires?.has(opt.value) && <TagObligatoire />}
              </span>
            )}
          />
        </Field>

        <Field label={t('gedUploader.champs.dateDocument')}>
          <DatePicker
            value={dateDocument}
            onChange={(d) => onUpdate({ dateDocument: d })}
            format="DD/MM/YYYY"
            disabled={disabled}
            style={{ width: '100%' }}
          />
        </Field>

        <Field label={t('gedUploader.champs.victime')} required={victimes.length > 0}>
          <Select value={victimeId || undefined} onChange={(v) => onUpdate({ victimeId: v, dossierReclamationId: '' })}
            placeholder={victimes.length ? t('gedUploader.champs.victimeConcernee') : t('gedUploader.champs.aucuneVictime')}
            disabled={disabled || !victimes.length}
            allowClear
            style={{ width: '100%', borderColor: victimes.length > 0 && !victimeId ? '#f59e0b' : undefined }}>
            {victimes.map(v => (
              <Option key={v.victimeTrackingId} value={v.victimeTrackingId}>
                {v.nom} {v.prenoms}
              </Option>
            ))}
          </Select>
          {victimes.length > 0 && !victimeId && (
            <div style={{ fontSize: 11, color: '#b45309', marginTop: 3 }}>
              {t('gedUploader.champs.requisAutoAssociation')}
            </div>
          )}
        </Field>

        <Field label={t('gedUploader.champs.dossierReclamation')}>
          <Select
            value={dossierReclamationId || undefined}
            onChange={(v) => onUpdate({ dossierReclamationId: v })}
            placeholder={victimeId ? (dossiersReclamation.length ? t('gedUploader.champs.choisirOuAuto') : t('gedUploader.champs.seraCreeAuto')) : t('gedUploader.champs.victimeDabord')}
            disabled={disabled || !victimeId}
            allowClear
            style={{ width: '100%' }}>
            {dossiersReclamation.map(d => (
              <Option key={d.dossierTrackingId} value={d.dossierTrackingId}>
                {d.numeroDossier}
              </Option>
            ))}
          </Select>
        </Field>

        <Field label={t('gedUploader.champs.tagsAdditionnels')}>
          <Select
            mode="tags"
            value={tagsExtra}
            onChange={(v) => onUpdate({ tagsExtra: v })}
            placeholder={t('gedUploader.champs.tagsSupplementaires')}
            disabled={disabled}
            style={{ width: '100%' }}
            tagRender={({ label, closable, onClose }) => (
              <Tag color="green" closable={closable} onClose={onClose} style={{ borderRadius: 20 }}>{label}</Tag>
            )}>
            {TAGS_SUGGERES.map(t => <Option key={t} value={t}>{t}</Option>)}
          </Select>
        </Field>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </div>
      {children}
    </div>
  );
}
